/**
 * `ez.a.send_webhook` — the generic outgoing webhook (the 12 `webhooks` static recipes, **42
 * automations**, the largest external surface on the account,
 * `discovery/feature-usage-matrix.md`).
 *
 * monday publishes no block id for these: all twelve are `static`, all twelve have an **empty**
 * `config`, and the destination URL lives in the `webhooks` app's credential store
 * (`auth.webhooks: 17680674` + `metadata.api_app`), which `board_automations` never returns.
 * That is why every one of the 42 imports **partial**: the recipe is fully described, the URL is
 * not exportable and an admin has to paste it once (`specs/M6-A4-integrations.md` §6).
 *
 * The body is documented in the spec and versioned: `{event: {...}}`, one top-level key, the
 * same envelope monday uses for its public-API webhooks (`discovery/api-corpus/webhooks.md`
 * §7) plus `automationId`. `deliveryId` is stable across retries, like monday's `triggerUuid`.
 * Signing is optional and is **ours**, not monday's — monday sends no signature at all (§3);
 * when a secret is set we add `x-elizmonday-signature: sha256=<hex>` over the exact bytes sent.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
export const sendWebhookParams = z.object({
    /** Empty on every imported row: monday does not export it. */
    url: z.string().default(''),
    /** Optional HMAC secret; never logged, never rendered in a sentence. */
    secret: z.string().nullable().default(null),
    /** monday's recipe name, e.g. `change_column_value`, so a consumer can route as before. */
    eventName: z.string().default('automation'),
});
/** The body, built purely from the run — no service calls, so it is unit-testable as a function. */
export function webhookBody(input) {
    const num = (value) => (value === null ? null : Number(value));
    return {
        event: {
            app: 'elizmonday',
            type: input.eventName,
            triggerTime: input.triggerTime.toISOString(),
            triggerUuid: input.deliveryId,
            isRetry: input.isRetry,
            boardId: num(input.boardId),
            pulseId: num(input.itemId),
            pulseName: input.itemName,
            userId: num(input.userId),
            automationId: Number(input.automationId),
            value: input.payload.value ?? null,
            previousValue: input.payload.previousValue ?? null,
        },
    };
}
export const sendWebhookAction = defineAction({
    id: 'ez.a.send_webhook',
    kind: 'action',
    app: 'webhooks',
    /**
     * **A deliberate difference from monday.** monday's card reads just *"send a webhook"* and
     * keeps the URL inside the `webhooks` app rather than in the sentence, which is exactly why
     * the URL is not exportable and all 42 rows import `partial`. Ours is a slot in the sentence,
     * so a person can see and set it where the rest of the recipe is (`specs/M6-A4` §6).
     */
    sentencePartial: 'send a webhook to {URL, url}',
    paramsSchema: sendWebhookParams,
    executable: true,
    assumed: true,
    references: () => [],
    async execute(context, params) {
        if (params.url.length === 0)
            return { sent: false, reason: 'no_url' };
        const integrations = context.services.integrations;
        if (integrations === undefined)
            return { sent: false, reason: 'unsupported' };
        const item = context.itemId === null ? null : await context.services.getItem(context.itemId);
        const body = webhookBody({
            eventName: params.eventName,
            automationId: context.automationId,
            boardId: context.boardId,
            itemId: context.itemId,
            itemName: item?.name ?? null,
            userId: context.event.actorId,
            payload: context.event.payload,
            triggerTime: context.now,
            deliveryId: context.event.id,
            isRetry: false,
        });
        const result = await integrations.sendWebhook({
            url: params.url,
            secret: params.secret,
            payload: body,
            deliveryId: context.event.id,
        });
        if (!result.ok)
            throw new Error(`webhook POST failed (${result.status}): ${result.detail ?? 'no detail'}`);
        // The URL is echoed back by host only: a query-string token in a webhook URL is a credential.
        return { sent: true, host: safeHost(params.url), status: result.status, attempts: result.attempts };
    },
    retryable: (error) => /\((0|408|429|5\d\d)\)/.test(error instanceof Error ? error.message : ''),
});
export function safeHost(url) {
    try {
        return new URL(url).host;
    }
    catch {
        return 'invalid-url';
    }
}
//# sourceMappingURL=ez.a.send-webhook.js.map